import React, { useState } from 'react';
import { Idea, ideaApi } from '../services/api';
import { LinkedIdeas } from './LinkedIdeas';
import toast from 'react-hot-toast';

interface IdeaDetailProps {
  idea: Idea;
  onClose: () => void;
  onUpdate: () => void;
  onIdeaClick?: (idea: Idea) => void;
}

export const IdeaDetail: React.FC<IdeaDetailProps> = ({ idea, onClose, onUpdate, onIdeaClick }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(idea.content);

  const handleSave = async () => {
    try {
      await ideaApi.update(idea.id, { content: editedContent });
      toast.success('Idea updated!');
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to update idea');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this idea?')) {
      try {
        await ideaApi.delete(idea.id);
        toast.success('Idea deleted');
        onClose();
        onUpdate();
      } catch (error) {
        toast.error('Failed to delete idea');
      }
    }
  };

  return (
    <div className="idea-detail">
      <div className="detail-header">
        <h2>Idea Details</h2>
        <button onClick={onClose} className="close-button">×</button>
      </div>
      
      <div className="detail-body">
        <div className="detail-section">
          <h3>Original Thought</h3>
          {isEditing ? (
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="edit-textarea"
              rows={4}
            />
          ) : (
            <p>{idea.content}</p>
          )}
        </div>

        {idea.summary && (
          <div className="detail-section">
            <h3>Summary</h3>
            <p>{idea.summary}</p>
          </div>
        )}

        {idea.expanded_content && (
          <div className="detail-section">
            <h3>Expanded Details</h3>
            <div className="expanded-content">{idea.expanded_content}</div>
          </div>
        )}

        <div className="detail-meta">
          {idea.category && <span className="meta-item">Category: {idea.category}</span>}
          {idea.priority && <span className="meta-item">Priority: {idea.priority}/10</span>}
          {idea.audio_path && <span className="meta-item">🎙️ Voice note</span>}
        </div>

        <div className="detail-actions">
          {isEditing ? (
            <>
              <button onClick={handleSave} className="save-button">Save</button>
              <button onClick={() => setIsEditing(false)} className="cancel-button">Cancel</button>
            </>
          ) : (
            <>
              <button onClick={() => setIsEditing(true)} className="edit-button">Edit</button>
              <button onClick={handleDelete} className="delete-button">Delete</button>
            </>
          )}
        </div>

        {onIdeaClick && idea.processed && (
          <LinkedIdeas 
            ideaId={idea.id} 
            onIdeaClick={onIdeaClick}
          />
        )}
      </div>
    </div>
  );
};